/**
 * A server's binary schema, fetched by its SHA-256 from the schema API
 * (`GET /api/flatbuffers/schemas/{sha256}`) and checked: its bytes must hash to
 * the SHA-256 asked for, and a registry signature, when the server sends one,
 * is verified against the registry's public keys.
 *
 * A schema is content-addressed, so bytes that hash right are the schema
 * whoever served them; the signature only says where it came from. It is
 * checked and reported, never required.
 */

import { SIGNING_KEYS, type SigningKey } from "../generated/schema-signing-keys";
import {
  SCHEMA_KEY_ID_HEADER,
  SCHEMA_PROVENANCE_HEADER,
  SCHEMA_ROOT_HEADER,
  SCHEMA_SIGNATURE_HEADER,
} from "../schemaHeaders";
import { sha256Hex } from "../sha256";

/**
 * What a schema's registry signature says:
 * - `verified`: it verifies against the registry key it names.
 * - `invalid`: it names a known key and does not verify.
 * - `unknown-key`: it names a key this bundle does not have.
 * - `unsigned`: the server sent none (a compiled or peer-announced schema).
 * - `unverifiable`: this page cannot verify one (no WebCrypto: not a secure context).
 */
export type SignatureStatus = "verified" | "invalid" | "unknown-key" | "unsigned" | "unverifiable";

/** A schema fetched from the schema API, its bytes checked against its SHA-256. */
export interface FetchedSchema {
  /** The SHA-256 the schema was asked for, which its bytes hash to. */
  hash: string;
  bfbs: Uint8Array;
  /** Its root type, e.g. `BatchedFrontEnd.BatchedFrontEndMessage`. */
  rootType: string;
  /** Where the server got it: `compiled`, `known`, `ci-signed`, `peer-announced`. */
  provenance: string;
  signature: SignatureStatus;
}

/** The message a registry signature signs (LiftCloud `schemaregistry.SignedMessage`). */
export function signedMessage(hash: string, rootType: string): Uint8Array<ArrayBuffer> {
  return new TextEncoder().encode(`LN2-SCHEMA-V1\n${hash}\n${rootType}`);
}

function base64Bytes(base64: string): Uint8Array<ArrayBuffer> {
  const binary = atob(base64);
  return Uint8Array.from(binary, (char) => char.charCodeAt(0));
}

/** The DER bytes of a PEM block. */
function pemBody(pem: string): Uint8Array<ArrayBuffer> {
  return base64Bytes(pem.replace(/-----[A-Z ]+-----/g, "").replace(/\s+/g, ""));
}

/**
 * What a registry signature (standard base64 of r‖s) of `hash` and `rootType`
 * says, checked with `subtle` against `keys`.
 */
export async function verifySignature(
  hash: string,
  rootType: string,
  signature: string | null,
  keyId: string | null,
  keys: readonly SigningKey[] = SIGNING_KEYS,
  subtle: SubtleCrypto | undefined = globalThis.crypto?.subtle,
): Promise<SignatureStatus> {
  if (!signature) {
    return "unsigned";
  }
  const key = keys.find((candidate) => candidate.keyId === keyId);
  if (!key) {
    return "unknown-key";
  }
  if (!subtle) {
    return "unverifiable";
  }
  try {
    const publicKey = await subtle.importKey(
      "spki",
      pemBody(key.pem),
      { name: "ECDSA", namedCurve: "P-256" },
      false,
      ["verify"],
    );
    const valid = await subtle.verify(
      { name: "ECDSA", hash: "SHA-256" },
      publicKey,
      base64Bytes(signature),
      signedMessage(hash, rootType),
    );
    return valid ? "verified" : "invalid";
  } catch {
    return "invalid";
  }
}

/** How long a schema the server could not serve is left before it is asked again. */
const RETRY_AFTER_MS = 5 * 60 * 1000;

interface CachedSchema {
  schema: Promise<FetchedSchema | null>;
  /** When to ask again: never, once a schema came back. */
  expires: number;
}

const schemas = new Map<string, CachedSchema>();

async function download(
  baseUrl: string,
  hash: string,
  authorization: string | undefined,
): Promise<FetchedSchema | null> {
  const headers: Record<string, string> = { Accept: "application/octet-stream" };
  if (authorization) {
    headers.authorization = authorization;
  }
  let response: Response;
  let bfbs: Uint8Array;
  try {
    response = await fetch(`${baseUrl}/flatbuffers/schemas/${hash}`, { headers });
    if (!response.ok) {
      return null;
    }
    bfbs = new Uint8Array(await response.arrayBuffer());
  } catch {
    return null;
  }
  // Content addressing: bytes that do not hash to the SHA-256 asked for are not
  // that schema, whatever served them.
  if ((await sha256Hex(bfbs)) !== hash) {
    return null;
  }
  const rootType = response.headers.get(SCHEMA_ROOT_HEADER) ?? "";
  return {
    hash,
    bfbs,
    rootType,
    provenance: response.headers.get(SCHEMA_PROVENANCE_HEADER) ?? "",
    signature: await verifySignature(
      hash,
      rootType,
      response.headers.get(SCHEMA_SIGNATURE_HEADER),
      response.headers.get(SCHEMA_KEY_ID_HEADER),
    ),
  };
}

/**
 * The schema whose SHA-256 is `hash` (a checked one: `isSchemaHash`), from the
 * schema API at `baseUrl`, or null when it cannot be had or does not hash
 * right. Each schema is fetched once; one that could not be had is asked for
 * again after a while.
 */
export function fetchSchema(
  baseUrl: string,
  hash: string,
  authorization?: string,
): Promise<FetchedSchema | null> {
  const key = `${baseUrl}|${hash}`;
  const cached = schemas.get(key);
  if (cached && cached.expires > Date.now()) {
    return cached.schema;
  }
  const pending = download(baseUrl, hash, authorization);
  schemas.set(key, { schema: pending, expires: Number.POSITIVE_INFINITY });
  return pending.then((schema) => {
    if (schema === null && schemas.get(key)?.schema === pending) {
      schemas.set(key, { schema: pending, expires: Date.now() + RETRY_AFTER_MS });
    }
    return schema;
  });
}

/** Forgets every fetched schema (for tests). */
export function resetFetchedSchemas(): void {
  schemas.clear();
}
